import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import useCurrentUser from "../hooks/useCurrentProvider";
import "../styles/clinicStripeOnboarding.css";
import { SERVER_URL } from "../config";

interface ClinicStripeOnboardingProps {
  clinicId?: string;
  clinic: {
    id: string;
    stripeAccountId?: string | null;
  };
}

interface Overview {
  patientCnt: number;
  subscriptions: { active: number; total: number };
  medicationsOffered: { id: string; name: string }[];
}

const ClinicStripeOnboarding: React.FC<ClinicStripeOnboardingProps> = ({
  clinic,
  clinicId,
}) => {
  const [loading, setLoading] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [overview, setOverview] = useState<Overview | null>(null);
  const token = localStorage.getItem("token");
  const currentUser = useCurrentUser();

  /* -------- fetch overview when connected -------- */
  useEffect(() => {
    if (!clinic.stripeAccountId) return;
    (async () => {
      try {
        const { data } = await axios.get<Overview>(
          `${SERVER_URL}/api/clinic/${clinicId}/overview`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setOverview(data);
      } catch {
        toast.error("Could not load clinic overview");
      }
    })();
  }, [clinic.stripeAccountId, clinicId, token]);

  /* -------- onboarding helpers (unchanged) -------- */
  const handleCreateAccount = async (): Promise<string | null> => {
    if (!currentUser.user || !token) {
      toast.error("User not authenticated.");
      return null;
    }

    try {
      const { data } = await axios.post(
        `${SERVER_URL}/api/stripe/create-account`,
        {
          email: currentUser.user.email,
          userId: currentUser.user.id,
          clinicId: clinicId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const newId = data.accountId;
      setAccountId(newId);
      toast.success("Stripe account created and saved!");
      return newId;
    } catch (err: any) {
      toast.error(
        err.response?.data?.error || "Failed to create Stripe account"
      );
      return null;
    }
  };

  const handleStartOnboarding = async () => {
    setLoading(true);
    try {
      if (!token) {
        toast.error("Authentication token missing.");
        return;
      }
      const id = accountId || (await handleCreateAccount());
      if (!id) return;
      const { data } = await axios.post(
        `${SERVER_URL}/api/stripe/account_link`,
        { account: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(data.url, "_blank");
      toast.info("Stripe onboarding opened in a new tab.", {
        autoClose: false,
        closeOnClick: true,
      });
    } catch {
      toast.error("Failed to start onboarding.");
    } finally {
      setLoading(false);
    }
  };

  const openDashboard = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Not authenticated");
        return;
      }

      const { data } = await axios.post(
        `${SERVER_URL}/api/stripe/login-link`,
        { accountId: clinic.stripeAccountId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      window.open(data.url, "_blank");
    } catch (err: any) {
      toast.error(
        err.response?.data?.error || "Couldn’t open Stripe dashboard"
      );
    }
  };

  /* extra CSS hooks added — no logic changes */
  if (!clinic.stripeAccountId) {
    return (
      <div className="clinic-stripe-box setup">
        <h2 className="stripe-heading">Set Up Your Stripe Account</h2>
        <p className="stripe-description">
          In order to receive payments, your clinic must create a Stripe
          account.
        </p>

        <button
          onClick={handleStartOnboarding}
          disabled={loading}
          className="btn onboard-button"
        >
          {loading ? "Processing…" : "Start Onboarding"}
        </button>
      </div>
    );
  }

  return (
    <div className="clinic-stripe-box overview">
      <h2 className="stripe-heading">Clinic Overview</h2>

      {!overview ? (
        <p className="loading-text">Loading data…</p>
      ) : (
        <>
          <ul className="overview-list">
            <li className="overview-item">
              <strong className="label">Patients:</strong> {overview.patientCnt}
            </li>
            <li className="overview-item">
              <strong className="label">Subscriptions:</strong>{" "}
              {overview.subscriptions.active} active /
              {overview.subscriptions.total} total
            </li>
            <li className="overview-item">
              <strong className="label">Medications Offered:</strong>{" "}
              {overview.medicationsOffered.map((m) => m.name).join(", ") || "—"}
            </li>
          </ul>

          <button className="btn dashboard-button" onClick={openDashboard}>
            Open Stripe Dashboard
          </button>
        </>
      )}
    </div>
  );
};

export default ClinicStripeOnboarding;
